import { faX } from "@fortawesome/pro-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";

import { T } from "@repo/transifex";
import { priceFormatter } from "~/utils/price-formatter";
import { ProductDescription } from "~/components/kiosk/product-detail/product-description";
import { AllowanceProductBadge } from "~/components/kiosk/products/allowance-product-badge";
import { Modal } from "~/components/kiosk/ui/modals/shared/modal";
import { ProductImage } from "~/components/kiosk/ui/product-image/product-image";
import { RoundButton } from "~/components/kiosk/ui/round-button/round-button";
import { kioskActions } from "~/stores/kiosk-session/actions";
import { AddToOrderButton } from "~/components/kiosk/product-detail/add-to-order-button";
import { splitAllowanceUnits } from "~/lib/sale-engine";
import { cn } from "~/lib/utils";
import { useKioskProducts } from "~/stores/kiosk-session/hooks/use-kiosk-products";
import { getDisplayProductBasketDiscount } from "~/stores/kiosk-session/hooks/use-kiosk-products/get-display-product-basket-discount";
import { getIsAddDisabled } from "~/stores/kiosk-session/hooks/use-kiosk-products/get-is-add-disabled";
import { type KioskDisplayProductList } from "~/stores/kiosk-session/hooks/use-kiosk-products/types";
import { useKioskSessionLocale } from "~/stores/kiosk-session/hooks/use-kiosk-session";
import { QuantityInputControl } from "~/components/Inputs/quantity-input-control/quantity-input-control";
import { ProductTagsWrapper } from "~/components/kiosk/product-tags/product-tags";
import { VariantHint } from "~/components/kiosk/product-detail/variant-hint";
import { AllowanceHint } from "~/components/kiosk/product-detail/allowance-hint";
import { OrderMinimumHint } from "~/components/kiosk/product-detail/order-minimum-hint";
import { VariantSection } from "~/components/kiosk/product-detail/variant-section";
import { DEFAULT_LANGUAGE } from "~/utils/constants";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { AllergensSection } from "~/components/kiosk/product-detail/allergens-section";
import { KioskNutritionSection } from "~/components/kiosk/product-detail/nutrition-climate/kiosk-nutrition-section";
import { hasNutritionClimate } from "~/components/kiosk/product-detail/nutrition-climate/nutrition-climate-utils";
import { getUnitSystemLabel } from "~/utils/get-unit-system-label";
import { calculateUnitPriceByUnitSystem } from "~/utils/calculateUnitPriceByUnitSystem";
import { useAppContext } from "~/providers/app";
import { useSendWebEvent } from "~/hooks";

import { useProductDetail } from "../__app.kiosk.products.$productId/product-detail-context";

import { parseVariantFormData } from "./parse-variant-form-data";
import { getVariantsTotalGrossPrice } from "./get-variants-total-gross-price";
import { VariantForm } from "./variant-form";
import { WeightPreview } from "./weight-preview";

export type VariantFormData = Record<string, number>;

export type ProductFormData = {
  quantity: number;
  variants: Record<string, VariantFormData | null> | null;
};

type AllowanceHintIntent = "limitReached" | "usedUp" | "budgetInfo" | null;

const getAllowanceHintIntent = (
  item: KioskDisplayProductList,
  fullQuantity: number
): AllowanceHintIntent => {
  if (item.product.type === "weighted" || !item.allowance) return null;
  if (item.allowance.isAllowanceOver) return "usedUp";
  if (fullQuantity > 0) return "limitReached";
  if (item.allowance.type === "unitPrice") return "budgetInfo";
  return null;
};

export const KioskProductDetailsPage = () => {
  const navigate = useNavigate();
  const sendWebEvent = useSendWebEvent();

  const { item, stampCardDiscount, close, weight } = useProductDetail();
  const showNutritionSection = hasNutritionClimate(
    item.nutrition,
    item.co2Rating
  );
  const { currency } = useKioskProducts();

  const locale = useKioskSessionLocale();

  const {
    hardware: {
      scales: { scale, unit: scaleUnit, decimals: scaleDecimals }
    }
  } = useAppContext();

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<ProductFormData>({
    defaultValues: {
      quantity: 1,
      variants: null
    }
  });

  const variants = useWatch({ control, name: "variants" });
  const quantity = useWatch({ control, name: "quantity" });

  const { data: till } = useCurrentTill();
  const hidePrices = till?.moduleConfig.hidePrices ?? false;

  const languageCode = till?.language ?? DEFAULT_LANGUAGE;

  const totalUnitVariantGrossPrice =
    item.product.type === "variant"
      ? getVariantsTotalGrossPrice(item.product.variants, variants)
      : 0;

  const weightedProductPrice =
    item.product.type === "weighted" && weight
      ? calculateUnitPriceByUnitSystem({
          unitPrice: item.unitDisplayPrice,
          weight: weight.weight,
          unitSystem: item.product.unitSystem,
          weightUnit: weight.unit
        })
      : null;

  const { discount, allowanceCap } = getDisplayProductBasketDiscount(
    item,
    stampCardDiscount
  );

  const { discountedQuantity, fullQuantity } = splitAllowanceUnits(
    quantity,
    allowanceCap
  );
  const unitDiscountedGrossPrice =
    item.unitDisplayPrice + totalUnitVariantGrossPrice;
  const unitFullGrossPrice =
    (item.unitDisplayListPrice ?? item.unitDisplayPrice) +
    totalUnitVariantGrossPrice;
  const splitGrossPrice =
    discountedQuantity * unitDiscountedGrossPrice +
    fullQuantity * unitFullGrossPrice;

  const totalProductGrossPrice = weightedProductPrice ?? splitGrossPrice;

  const allowanceHintIntent = getAllowanceHintIntent(item, fullQuantity);

  const onSubmit = (data: ProductFormData) => {
    if (item.product.type === "variant") {
      kioskActions.basket.add(item, {
        quantity,
        choices: parseVariantFormData(data.variants),
        discount
      });
    } else if (item.product.type === "weighted") {
      if (!weight) {
        // Connect to scale before navigating to weight page (similar to attended mode)
        if (scale) {
          const scaleData = "model" in scale ? scale.scale : scale;
          sendWebEvent({
            type: "SCALE_CONNECT_REQUEST",
            payload: { ...scaleData, unit: scaleUnit, decimals: scaleDecimals }
          });
        }
        navigate("weight");
        return;
      }

      kioskActions.basket.add(item, { weight, discount });
    } else {
      kioskActions.basket.add(item, { quantity, discount });
    }

    close();
  };

  const isIncrementDisabled = getIsAddDisabled(item, {
    quantity,
    stampCardDiscount
  });
  // Block the submit entirely when the product can't be added at all (limit/stock
  // reached). Does not pass stampCardDiscount — the modal IS the valid add path
  // for stamp-card rewards, so those should still be submittable.
  const isSubmitDisabled = getIsAddDisabled(item);

  return (
    <Modal backdropVariant="idle" onOutsideClick={close}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-[1320px] w-[720px] flex-col overflow-hidden rounded-3xl bg-white"
      >
        <div className="min-h-0 flex-1 overflow-y-auto">
          <ProductImage
            alt={item.product.name}
            image={item.product.imageUrl}
            className="h-[480px] !rounded-none"
            height="480"
          >
            <div className="flex h-full flex-col">
              <div className="flex items-end justify-end p-8">
                <RoundButton icon={faX} onClick={close} />
              </div>
              <div className="flex flex-1 items-end p-8">
                <ProductTagsWrapper
                  tags={item.product.tags}
                  languageCode={languageCode}
                />
              </div>
            </div>
          </ProductImage>

          <div
            className={cn(
              "flex flex-col items-start gap-4 px-10 py-9",
              !showNutritionSection && "border-b"
            )}
          >
            <p className="line-clamp-2 text-5xl font-bold">
              {item.product.name}
            </p>

            {item.allowance &&
            !item.allowance.isAllowanceOver &&
            !hidePrices ? (
              <div className="flex items-center gap-3 text-3xl">
                <AllowanceProductBadge
                  price={priceFormatter({
                    value: item.unitDisplayPrice,
                    currency,
                    locale
                  })}
                />

                {item.unitDisplayListPrice ? (
                  <p className="text-kiosk-text-secondary line-through">
                    {priceFormatter({
                      value: item.unitDisplayListPrice,
                      currency,
                      locale
                    })}
                  </p>
                ) : null}
              </div>
            ) : null}

            <OrderMinimumHint orderMin={item.product.orderMin} />

            {item.product.description ? (
              <ProductDescription
                key={item.product.id}
                description={item.product.description}
              />
            ) : null}

            <AllergensSection
              allergens={item.product.allergens.contains}
              languageCode={languageCode}
            >
              <T _str="Contains" />
            </AllergensSection>

            <AllergensSection
              allergens={item.product.allergens.mayContain}
              languageCode={languageCode}
            >
              <T _str="May contain" />
            </AllergensSection>
          </div>

          <KioskNutritionSection
            nutrition={item.nutrition}
            co2Rating={item.co2Rating}
          />

          {item.product.type === "variant"
            ? item.product.variants.map((variant) => (
                <VariantSection
                  key={variant.id}
                  name={variant.name}
                  className="py-2"
                >
                  <Controller
                    name={`variants.${variant.id}`}
                    control={control}
                    rules={{
                      validate: (choices) => {
                        if (!variant.minimum) return true;

                        let totalQuantity = 0;
                        for (const key in choices) {
                          totalQuantity += choices[key];
                        }

                        return totalQuantity > 0 ? true : "required";
                      }
                    }}
                    render={({ field }) => (
                      <>
                        <VariantHint
                          minimum={variant.minimum}
                          maximum={variant.maximum}
                          error={Boolean(errors.variants?.[variant.id])}
                        />
                        <VariantForm
                          variant={variant}
                          variantFormData={field.value}
                          currency={currency}
                          locale={locale}
                          hidePrices={hidePrices}
                          onChange={field.onChange}
                        />
                      </>
                    )}
                  />
                </VariantSection>
              ))
            : null}
        </div>

        <div className="flex w-full flex-col border-t bg-white">
          {allowanceHintIntent === "limitReached" && !hidePrices ? (
            <AllowanceHint
              intent="limitReached"
              fullQuantity={fullQuantity}
              fullUnitPrice={priceFormatter({
                value: unitFullGrossPrice,
                currency,
                locale
              })}
            />
          ) : null}

          {allowanceHintIntent === "usedUp" ||
          allowanceHintIntent === "budgetInfo" ? (
            <AllowanceHint intent={allowanceHintIntent} />
          ) : null}

          <div className="flex min-h-36 w-full items-center justify-center gap-4 px-5">
            {item.product.type !== "weighted" && (
              <Controller
                name="quantity"
                control={control}
                render={({ field }) => (
                  <QuantityInputControl
                    incrementDisabled={isIncrementDisabled}
                    quantity={field.value}
                    onIncrementClick={() => {
                      field.onChange(field.value + 1);
                    }}
                    onDecrementClick={() => {
                      if (field.value > 1) {
                        field.onChange(field.value - 1);
                      }
                    }}
                    variant="secondary"
                    size="lg"
                  />
                )}
              />
            )}

            {item.product.type === "weighted" && weight ? (
              <WeightPreview weight={weight} />
            ) : null}

            <AddToOrderButton
              type="submit"
              disabled={isSubmitDisabled}
              price={
                hidePrices
                  ? undefined
                  : priceFormatter({
                      value: totalProductGrossPrice,
                      currency,
                      locale
                    })
              }
              unitSystemLabel={
                item.product.type === "weighted" && !weight
                  ? getUnitSystemLabel(item.product.unitSystem)
                  : undefined
              }
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};
